'use client';

import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { useI18n } from '@/components/i18n-provider';
import { Alert, Button, Field, Input } from '@/components/ui';
import { apiClient } from '@/lib/api-client';

/**
 * Staff sign-in.
 *
 * Hotel accounts live in the same Payload `users` collection as Studio Present
 * ones but never reach the admin panel, so this is their only door in. The
 * password is posted to our own route, which exchanges it for a session cookie
 * the browser cannot read.
 *
 * The submit button used to be a hand-written `<button>` with its own height,
 * radius, focus ring and disabled opacity — the single largest control in the
 * product, and the one furthest from the component library. It is a `Button`
 * now, in the accent variant, which is the same treatment Approve gets on the
 * board: one decisive action per screen, in the accent.
 */
export function LoginForm() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);

    try {
      await apiClient.login(email, password);

      /**
       * A hard navigation, deliberately — not `router.push`.
       *
       * The session arrives as an httpOnly cookie set by our own route, which
       * the router knows nothing about, so every entry in its cache was
       * rendered signed-out. This used to be `push` followed by `refresh`, and
       * the two raced: `push` fetched `/touristtax`, while `refresh` refetched
       * the route still on screen — the LOGIN page, whose server component now
       * saw a valid cookie and answered with a redirect to `/touristtax`. Two
       * navigations to the same place, one of them a redirect, arriving in
       * either order. The transition never settled, the button span on (there
       * is no `setPending(false)` on the success path, because the page is
       * meant to be leaving), and a manual reload fixed it instantly — which
       * was the tell: the server render was never slow, only the client
       * navigation was stuck.
       *
       * `assign` throws all of that away and asks the server for the page once,
       * with the cookie attached. It costs a full document load, on a screen
       * that is being left behind exactly once per session.
       */
      window.location.assign('/touristtax');
    } catch (loginError) {
      // The CMS writes its rejections in English. Only the generic failure is
      // translated — a message explaining *why* a specific sign-in was refused
      // is worth more in the wrong language than a vague one in the right one.
      setError(loginError instanceof Error ? loginError.message : t.login.failed);
      setPending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Sized against the fields rather than against nothing. The controls
          below are held at 16px on a phone so iOS does not zoom the viewport
          on focus, which left a 19px heading and a 14px line of prose sitting
          under a placeholder that was larger than either — the form's least
          important text reading as its loudest. */}
      <div className="mb-8">
        <h2 className="type-display-sm">{t.login.title}</h2>
        {/* No hotel named here any more. Every property signs in at this one
            address, so the name is not known until the account is — see the
            picker at `/touristtax/hotels`. */}
        <p className="type-body-lg mt-2 text-foreground-muted">{t.login.subtitle}</p>
      </div>

      {/* `sm:text-base` on every control here, against the shared rule that
          drops form text to 14px above `sm`.

          That rule is right for the board and for the guest form, which are
          dense: many fields, read at a glance, where 14px buys rows on screen.
          This screen has two fields and one decision, so the density it buys is
          worth nothing — and 14px inside a 40px control left the whole form
          reading as small print on a desktop monitor. */}
      <form onSubmit={submit} className="space-y-5">
        <Field id="email" label={t.login.emailLabel}>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            autoFocus
            required
            inputSize="lg"
            className="sm:text-base"
            placeholder={t.login.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field id="password" label={t.login.passwordLabel}>
          <div className="relative">
            <Input
              id="password"
              // Revealing beats a second "confirm" field on a phone at a
              // reception desk, where the password is usually being typed by
              // someone standing up.
              type={reveal ? 'text' : 'password'}
              autoComplete="current-password"
              required
              inputSize="lg"
              className="pr-10 sm:text-base"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setReveal((r) => !r)}
              aria-label={reveal ? t.login.hidePassword : t.login.showPassword}
              aria-pressed={reveal}
              // Centred with auto margins, not `top-1/2 -translate-y-1/2`.
              // Every button carries `active:translate-y-px` — the one-pixel
              // press — and that rule replaces the translate doing the
              // centring, so the icon used to drop half its own height the
              // moment it was pressed. Auto margins leave the transform free
              // for the press it was written for.
              className="absolute inset-y-0 right-1 my-auto"
            >
              {reveal ? <EyeOff /> : <Eye />}
            </Button>
          </div>
        </Field>

        {error && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>
            <Alert variant="destructive">{error}</Alert>
          </motion.div>
        )}

        <Button
          type="submit"
          variant="accent"
          size="lg"
          loading={pending}
          className="group w-full text-base"
        >
          {t.login.submit}
          <ArrowRight className="transition-transform duration-(--duration-fast) ease-(--ease-out) group-hover:translate-x-0.5" />
        </Button>
      </form>
    </motion.div>
  );
}
